import { getTestGroupKey } from "@/features/person/components/PGMProgramTable/utils";
import type { StudentTestResult } from "./types";

export type StudentTestPassMetric = {
  label: string;
  value: string;
  iconName: "description";
  variant?: "success";
  testId: string;
};

const levelNumber = (level: string): string => level.match(/\d+$/)?.[0] || level;

const compareLevels = (left: string, right: string): number => {
  const leftNumber = Number(left.match(/\d+$/)?.[0]);
  const rightNumber = Number(right.match(/\d+$/)?.[0]);
  const leftRank = Number.isNaN(leftNumber) ? Number.POSITIVE_INFINITY : leftNumber;
  const rightRank = Number.isNaN(rightNumber) ? Number.POSITIVE_INFINITY : rightNumber;
  if (leftRank !== rightRank) return leftRank - rightRank;
  return left.localeCompare(right);
};

const uniqueTestGroupKey = (result: StudentTestResult): string =>
  `${result.level}:${getTestGroupKey({
    testId: result.testId,
    testDefinition: { oldTestId: result.oldTestId }
  })}`;

export const buildStudentTestPassMetrics = (results: StudentTestResult[]): StudentTestPassMetric[] => {
  const uniqueTests = new Map<string, { level: string; passed: boolean }>();

  for (const result of results) {
    const groupKey = uniqueTestGroupKey(result);
    const existing = uniqueTests.get(groupKey);
    if (!existing) {
      uniqueTests.set(groupKey, { level: result.level, passed: result.passed });
    } else if (result.passed) {
      existing.passed = true;
    }
  }

  const uniqueResults = Array.from(uniqueTests.values());
  if (uniqueResults.length === 0) return [];

  const levelGroups = uniqueResults.reduce(
    (acc, result) => {
      if (!acc[result.level]) {
        acc[result.level] = { passed: 0, total: 0 };
      }
      acc[result.level].total++;
      if (result.passed) {
        acc[result.level].passed++;
      }
      return acc;
    },
    {} as Record<string, { passed: number; total: number }>
  );

  const totalPassed = uniqueResults.filter((result) => result.passed).length;
  const totalTests = uniqueResults.length;

  const levelMetrics = Object.entries(levelGroups)
    .sort(([left], [right]) => compareLevels(left, right))
    .map(([level, data]) => {
      const number = levelNumber(level);
      return {
        label: `Level ${number} Tests Passed`,
        value: `${data.passed}/${data.total}`,
        iconName: "description" as const,
        variant: data.passed === data.total ? ("success" as const) : undefined,
        testId: `student-test-pass-metric-level-${number}`
      };
    });

  return [
    ...levelMetrics,
    {
      label: "Total Tests Passed",
      value: `${totalPassed}/${totalTests}`,
      iconName: "description" as const,
      variant: totalPassed === totalTests ? ("success" as const) : undefined,
      testId: "student-test-pass-metric-total"
    }
  ];
};
